import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error("[AppErrorBoundary]", error, info.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <main
          className="flex min-h-screen flex-col items-center justify-center gap-6 px-6"
          style={{ background: "#FAFAFB", fontFamily: "'DM Sans', system-ui, sans-serif" }}
        >
          <div
            className="flex h-14 w-14 items-center justify-center rounded-xl text-2xl font-black text-white"
            style={{ background: "linear-gradient(135deg, #6366F1, #3B82F6)" }}
            aria-hidden
          >
            V
          </div>
          <div className="max-w-md text-center">
            <h1 className="text-xl font-semibold text-slate-900">We hit an app error</h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Reload the page to continue. If this keeps happening, email{" "}
              <a href="mailto:support@vincimindlabs.com" className="font-medium text-slate-700 underline">
                support@vincimindlabs.com
              </a>{" "}
              and we&apos;ll help.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-95"
              style={{ background: "linear-gradient(135deg, #6366F1, #3B82F6)" }}
            >
              Reload page
            </button>
            <a
              href="mailto:support@vincimindlabs.com"
              className="text-sm font-medium text-slate-600 underline underline-offset-2"
            >
              Email support
            </a>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
